import * as React from 'react';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import type {DropResult} from 'react-beautiful-dnd';

import {Button} from 'shells/button/button';
import {ButtonMover} from 'shells/button-mover/button-mover';

import {SpecialCaseItem} from './special-case-item';
import type {UserAssignmentAutomation} from './user-assignments-types';

type Props = {
    specialCases: UserAssignmentAutomation[];
    onClickAdd: () => void;
    onReorder: (from: number, to: number) => void;
    onRemove: (index: number) => void;
};

function getDraggingStyle(isDragging: boolean, draggableStyle: any) {
    return {
        margin: '0 0 8px 0',
        // styles we need to apply on draggables
        ...draggableStyle,
    };
}

// This is kind of a (hopefully) temporary function to hash the stringified
// config object, so that we can use it as an id and key, since conditions have no id.
function hashCode(input: string) {
    let hash = 0,
        i,
        chr;
    if (!input || input.length === 0) return hash;
    for (i = 0; i < input.length; i++) {
        chr = input.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0; // Convert to 32bit integer
    }

    return hash;
}

// Special cases aren't easily serializable to an id, so this function takes
// care of that, so that we can set a unique `key` and `draggableId`.
function makeKeyFromSpecialCase(specCase: UserAssignmentAutomation): string {
    const assigneeId = specCase.assignee ? specCase.assignee.id : 'unassign';
    const criteriaHash = hashCode(JSON.stringify(specCase.criteria || ''));

    return `${assigneeId}:${criteriaHash}`;
}

// This must extend Component rather thenPure component while it remains the
// direct parent of Droppable. Otherwise we have issues with dropping things
// in positions outside the original viewport
export class SpecialCaseList extends React.Component<Props> {
    render() {
        return (
            <DragDropContext onDragEnd={this.handleDragEnd}>
                <Droppable droppableId='specialCases'>
                    {(droppableProvided) => (
                        <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                            {this.props.specialCases.map((specCase, index) => {
                                const specialCaseId = makeKeyFromSpecialCase(specCase);
                                const criteria = specCase.criteria;

                                // This shouldn't happen, but for safety…
                                if (!criteria) return null;

                                return (
                                    <Draggable
                                        key={specialCaseId}
                                        index={index}
                                        draggableId={specialCaseId}
                                    >
                                        {(draggableProvided, draggableSnapshot) => {
                                            const style = getDraggingStyle(
                                                draggableSnapshot.isDragging,
                                                draggableProvided.draggableProps.style
                                            );

                                            return (
                                                <div>
                                                    <div
                                                        ref={draggableProvided.innerRef}
                                                        {...draggableProvided.draggableProps}
                                                        style={style}
                                                    >
                                                        <SpecialCaseItem
                                                            dragHandleProps={
                                                                draggableProvided.dragHandleProps
                                                            }
                                                            assignee={specCase.assignee}
                                                            shouldUnassign={specCase.shouldUnassign}
                                                            shouldRoundRobin={
                                                                specCase.shouldRoundRobin
                                                            }
                                                            criteria={criteria}
                                                            onRemove={() =>
                                                                this.props.onRemove(index)
                                                            }
                                                        />
                                                    </div>
                                                    {/* @ts-expect-error */}
                                                    {draggableProvided.placeholder}
                                                </div>
                                            );
                                        }}
                                    </Draggable>
                                );
                            })}
                            {droppableProvided.placeholder}
                            <ButtonMover direction='left' size='big'>
                                <Button
                                    variant='text-add'
                                    size='big'
                                    onClick={this.props.onClickAdd}
                                >
                                    Add an assignment rule
                                </Button>
                            </ButtonMover>
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        );
    }

    handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        this.props.onReorder(result.source.index, result.destination.index);
    };
}
